import React from 'react';
import { Star, BadgeCheck, Quote } from 'lucide-react';
import { useStore } from '../context/StoreContext';

const Stars = ({ rating }) => (
  <div className="flex">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`h-4 w-4 ${i < rating ? 'fill-[#f5b400] text-[#f5b400]' : 'text-gray-300'}`} />
    ))}
  </div>
);

const ReviewsSection = () => {
  const { reviews, products } = useStore();
  const productName = (id) => products.find((p) => p.id === id)?.name || 'FBD Cleaning';

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : '0.0';

  return (
    <section id="reviews" className="py-20 bg-secondary/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-primary font-semibold uppercase tracking-wide text-sm mb-2">Loved by Customers</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-foreground">What Our Customers Say</h2>
          <div className="mt-4 inline-flex items-center gap-3 bg-white rounded-full px-5 py-2.5 shadow-sm border border-border">
            <Stars rating={Math.round(avg)} />
            <span className="font-display font-bold text-foreground">{avg} / 5</span>
            <span className="text-sm text-muted-foreground">from {reviews.length} reviews</span>
          </div>
        </div>

        {reviews.length === 0 ? (
          <p className="text-center text-muted-foreground">No reviews yet.</p>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
            {reviews.map((r) => (
              <div key={r.id} className="break-inside-avoid bg-white rounded-2xl border border-border p-6 shadow-sm hover:shadow-md transition-shadow">
                <Quote className="h-6 w-6 text-primary/30" />
                <div className="flex items-center justify-between mt-2">
                  <Stars rating={r.rating} />
                  <span className="text-xs text-muted-foreground">{new Date(r.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
                <p className="mt-3 text-foreground/80 text-sm leading-relaxed">{r.comment}</p>

                {r.media && r.media.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {r.media.map((m, idx) =>
                      m.type === 'image' ? (
                        <img key={idx} src={m.url} alt="review" className="rounded-lg h-28 w-full object-cover border border-border" />
                      ) : m.url.includes('youtube') || m.url.includes('youtu.be') ? (
                        <div key={idx} className="col-span-2 aspect-video rounded-lg overflow-hidden border border-border">
                          <iframe title={`review-video-${idx}`} src={m.url} className="w-full h-full" allowFullScreen frameBorder="0" />
                        </div>
                      ) : (
                        <div key={idx} className="col-span-2 rounded-lg overflow-hidden border border-border">
                          <video src={m.url} controls className="w-full h-full" />
                        </div>
                      )
                    )}
                  </div>
                )}

                <div className="mt-4 flex items-center gap-3 pt-4 border-t border-border">
                  <div className="h-9 w-9 rounded-full fbd-green-gradient text-white flex items-center justify-center font-semibold text-sm">
                    {r.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground flex items-center gap-1">
                      {r.name}
                      {r.verified && <BadgeCheck className="h-4 w-4 text-primary" />}
                    </p>
                    <p className="text-xs text-muted-foreground">on {productName(r.productId)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ReviewsSection;
